// MultiCdmReview.js
// -----------------------------------------------------------------------------
// The end of a block: a sidebar of every case in it, one grade view at a time.
//
// Grades are fetched lazily — one call per case, the first time you open it,
// then cached for the rest of the visit. Opening a block doesn't fetch 13
// grades up front.
//
// Each case's grade is fetched by ITS OWN caseguid, so redoing a case outside
// the block later can't change what this screen shows.
//
// Props:
//   session          normalized session                        (required)
//   cases            merged case rows                          (required)
//   api              MultiCDM api layer                        (required)
//   userData         login payload                             (required)
//   userProfile      login profile                             (required)
//   reviewLaterIds   array of caseIds marked review-later      (required)
//   onReviewLater    (caseId, marked) => void                  (required)
//   readingOptions   saved reading options, or null            (optional)
//   onReadingOptions (next) => void                            (optional)
//   onExit           () => void — back to the session list     (required)
//   primaryColor     string                                    (optional)
//   selectedTheme    theme object                              (optional)
//   themeNumber      raw theme index, passed straight through to
//                    MultiCdmGradeView                                     (optional)
// -----------------------------------------------------------------------------

import React from "react";
import MDSpinner from "react-md-spinner";
import MultiCdmGradeView from "./MultiCdmGradeView";
import { CASE_STATUS } from "./multiCdmFetches";

class MultiCdmReview extends React.Component {
  constructor(props) {
    super(props);

    const graded = this.gradedEntries(props);
    this.state = {
      // caseguid -> grade envelope
      grades: {},
      // caseguid -> true while its fetch is in flight
      loading: {},
      // caseguid -> error string
      errors: {},
      selectedCaseId: graded.length > 0 ? graded[0].CaseId : null,
    };

    this._mounted = false;
    this._controllers = {};
  }

  componentDidMount() {
    this._mounted = true;
    const entry = this.selectedEntry();
    if (entry) this.loadGrade(entry);
  }

  componentWillUnmount() {
    this._mounted = false;
    // Drop any in-flight grade fetches — nothing is going to render them.
    Object.keys(this._controllers).forEach((key) => {
      this._controllers[key].abort();
    });
  }

  // Only completed cases have a grade to show. In a finished block that's all of
  // them, but a block can be reviewed while a case is still open if the user got
  // here another way, so filter rather than assume.
  gradedEntries(props) {
    const source = props || this.props;
    return source.session.CaseProgress.Cases.filter(function (entry) {
      return entry.Status === CASE_STATUS.COMPLETED && entry.caseguid;
    });
  }

  selectedEntry() {
    const selected = this.state.selectedCaseId;
    if (selected === null) return null;
    return (
      this.gradedEntries().find(function (entry) {
        return entry.CaseId === selected;
      }) || null
    );
  }

  caseRow(caseId) {
    const all = this.props.cases;
    for (let i = 0; i < all.length; i++) {
      if (all[i].ID === caseId) return all[i];
    }
    return null;
  }

  loadGrade(entry) {
    const guid = entry.caseguid;
    if (this.state.grades[guid]) return;
    if (this.state.loading[guid]) return;

    const controller = new AbortController();
    this._controllers[guid] = controller;

    this.setState(function (prevState) {
      const loading = Object.assign({}, prevState.loading);
      loading[guid] = true;
      const errors = Object.assign({}, prevState.errors);
      delete errors[guid];
      return { loading: loading, errors: errors };
    });

    this.props.api
      .retrieveMultiCdmCaseGrade(guid, controller.signal)
      .then((grade) => {
        delete this._controllers[guid];
        if (!this._mounted) return;
        this.setState(function (prevState) {
          const grades = Object.assign({}, prevState.grades);
          grades[guid] = grade;
          const loading = Object.assign({}, prevState.loading);
          delete loading[guid];
          return { grades: grades, loading: loading };
        });
      })
      .catch((error) => {
        delete this._controllers[guid];
        if (!this._mounted) return;
        if (error && error.name === "AbortError") return;
        this.setState(function (prevState) {
          const loading = Object.assign({}, prevState.loading);
          delete loading[guid];
          const errors = Object.assign({}, prevState.errors);
          errors[guid] =
            "That grade wouldn't load. Check your connection and try again.";
          return { loading: loading, errors: errors };
        });
      });
  }

  selectCase = (entry) => {
    this.setState({ selectedCaseId: entry.CaseId });
    this.loadGrade(entry);
  };

  renderBody() {
    const entry = this.selectedEntry();

    if (!entry) {
      return (
        <div className="mcdm-empty">
          <h2>Nothing to review yet</h2>
          <p>Finish a case and its grade shows up here.</p>
        </div>
      );
    }

    const guid = entry.caseguid;
    const grade = this.state.grades[guid];
    const error = this.state.errors[guid];
    const caseRow = this.caseRow(entry.CaseId);

    if (error) {
      return (
        <div className="mcdm-error">
          <p>{error}</p>
          <button
            type="button"
            className="mcdm-btn"
            onClick={() => {
              this.loadGrade(entry);
            }}
          >
            Try again
          </button>
        </div>
      );
    }

    if (!grade || !caseRow) {
      return (
        <div className="mcdm-loading">
          <MDSpinner size={50} singleColor={this.props.primaryColor} />
          <p>Loading grades</p>
        </div>
      );
    }

    return (
      <MultiCdmGradeView
        key={guid}
        grade={grade}
        caseRow={caseRow}
        api={this.props.api}
        userData={this.props.userData}
        readingOptions={this.props.readingOptions}
        onReadingOptions={this.props.onReadingOptions}
        reviewLater={this.props.reviewLaterIds.indexOf(entry.CaseId) !== -1}
        onReviewLater={this.props.onReviewLater}
        primaryColor={this.props.primaryColor}
        selectedTheme={this.props.selectedTheme}
        themeNumber={this.props.themeNumber}
        animationsDisabled={this.props.userProfile.animationsDisabled}
      />
    );
  }

  render() {
    const entries = this.gradedEntries();
    const { session, onExit } = this.props;

    return (
      <div className="mcdm-review">
        <div className="mcdm-review__side">
          <p className="mcdm-review__side-title">{session.Title}</p>
          {entries.map((entry, index) => {
            const row = this.caseRow(entry.CaseId);
            const on = this.state.selectedCaseId === entry.CaseId;
            // The block is over by now, so a hidden title has served its
            // purpose — show it here regardless of the setting.
            return (
              <button
                type="button"
                key={entry.CaseId}
                className={
                  "mcdm-review__item" + (on ? " mcdm-review__item--on" : "")
                }
                aria-current={on ? "true" : undefined}
                onClick={() => {
                  this.selectCase(entry);
                }}
              >
                <span className="mcdm-review__num">{index + 1}</span>
                <span className="mcdm-review__name">
                  {row ? row.TITLE : "Case " + entry.CaseId}
                </span>
                <span className="mcdm-review__grade">
                  {typeof entry.GradeForCase === "number"
                    ? Math.round(entry.GradeForCase) + "%"
                    : "\u2014"}
                </span>
              </button>
            );
          })}
          <div className="mcdm-review__side-foot">
            <button
              type="button"
              className="mcdm-btn mcdm-btn--sm"
              onClick={onExit}
            >
              Back to blocks
            </button>
          </div>
        </div>
        <div className="mcdm-review__body">{this.renderBody()}</div>
      </div>
    );
  }
}

export default MultiCdmReview;